import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import Page from '../../../components/Layout/Page';
import IndicatorsTable from '../components/table';
import { useYear } from '../../../hooks/useYear';
import { getStudentsByLevelLocation } from '../../../requests/api/dashboard';
import { DEFAULT_API_ERROR_MESSAGE } from '../../../utils/errors';
import useStyles from './styles';

interface StudentsByLevelLocationData {
  columns: Array<{ id: string; label: string }>;
  rows: Array<{
    id: string;
    label: string;
    values: Record<string, { amount: number; variation: number }>;
    rightValue?: number;
  }>;
}

const StudentsByLevelLocation: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { selectedYear } = useYear();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StudentsByLevelLocationData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getStudentsByLevelLocation(selectedYear);
        setData(response);
      } catch (err) {
        const e = err as AxiosError;
        enqueueSnackbar(e.response?.data?.message || DEFAULT_API_ERROR_MESSAGE, {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, enqueueSnackbar]);

  const handleGoBack = () => {
    history.push('/dashboard/academico');
  };

  const title = 'Alumnos por nivel';

  if (loading) {
    return (
      <Page title={title}>
        <Box className={classes.headerRow}>
          <Box className={classes.backButtonContainer}>
            <IconButton
              onClick={handleGoBack}
              className={classes.backButton}
              size="small"
            >
              <ArrowBackIos className={classes.backIcon} />
            </IconButton>
            <Typography className={classes.pageTitle}>
              {title}
            </Typography>
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  return (
    <Page title={title}>
      <Box className={classes.headerRow}>
        <Box className={classes.backButtonContainer}>
          <IconButton
            onClick={handleGoBack}
            className={classes.backButton}
            size="small"
          >
            <ArrowBackIos className={classes.backIcon} />
          </IconButton>
          <Typography className={classes.pageTitle}>
            {title}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.enrollmentsContainer}>
        {data && (
          <IndicatorsTable
            columns={data.columns}
            rows={data.rows}
            currencySymbol=""
          />
        )}
      </Box>
    </Page>
  );
};

export default StudentsByLevelLocation;
